import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { revealProps } from "../../common/Reveal";
import { toast } from "react-hot-toast";
import { PlayIcon, ArrowDownTrayIcon, CheckCircleIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import AiDisclaimer from "../AiDisclaimer";
import Button from "../../common/Button";
import Dropdown from "../../Settings/Dropdown";
import { useNowPlaying } from "../NowPlayingContext";
import AnalyzingState from "../AnalyzingState";
import TriageButton from "../TriageButton";
import LeadNoteModal from "../LeadNoteModal";
import { isNewSince, NEW_ROW_CLASS, NewPill, UpdatesBanner } from "../updates";
import { api, DISMISS_REASONS, downloadFile, formatMs, isReportLead, LEAD_CATEGORY_LABELS, LeadListItem, SEVERITY_CLASSES } from "../api";

const LeadsTab: React.FC<{ investigationId: string; reanalyzing?: boolean; updates?: number; since?: string; onSeen?: () => void; clearing?: boolean }> = ({ investigationId, reanalyzing, updates = 0, since, onSeen, clearing }) => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<LeadListItem[]>([]);
  // Docked player (shared across the investigation): play a lead's moment
  // without leaving the list.
  const { play, nowPlaying, playing: audioPlaying } = useNowPlaying();
  const [playingLeadId, setPlayingLeadId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  // The default view is the inbox: what's still waiting on a decision.
  const [status, setStatus] = useState("new");
  const [minSeverity, setMinSeverity] = useState(1);
  const [dismissing, setDismissing] = useState<LeadListItem | null>(null);
  // Leads animating out of the current view after a decision.
  // Two phases: the card swipes off and fades, then its row collapses.
  const [leaving, setLeaving] = useState<Record<string, { kind: "confirmed" | "dismissed"; phase: "swipe" | "collapse" }>>({});

  useEffect(() => {
    if (!dismissing) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setDismissing(null);
    const onDown = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest("[data-dismiss-chooser]")) setDismissing(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [dismissing]);

  const load = async () => {
    try {
      setLeads(await api.leads(investigationId));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigationId]);

  const matches = (l: LeadListItem) =>
    (!category || l.category === category) &&
    (status === "all" || (status === "open" ? l.status !== "dismissed" : l.status === status)) &&
    l.severity >= minSeverity;
  // Keep items that no longer match in the list while they animate out.
  const filtered = useMemo(() => leads.filter((l) => matches(l) || leaving[l.id]), [leads, category, status, minSeverity, leaving]); // eslint-disable-line react-hooks/exhaustive-deps
  // Evidence leads first; findings from official case documents (the
  // officer's own reports and filings) sit in their own section so they
  // never bury the call leads.
  const evidenceLeads = useMemo(() => filtered.filter((l) => !isReportLead(l)), [filtered]);
  const reportLeads = useMemo(() => filtered.filter(isReportLead), [filtered]);
  const ordered = useMemo(() => [...evidenceLeads, ...reportLeads], [evidenceLeads, reportLeads]);

  const setLeadStatus = async (lead: LeadListItem, next: string, dismiss_reason?: string) => {
    setDismissing(null);
    const willLeave = next !== "new" && !(status === "all" || (status === "open" && next === "confirmed") || status === next);
    try {
      const updated = await api.updateLead(investigationId, lead.id, { status: next, ...(dismiss_reason ? { dismiss_reason } : {}) });
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, ...updated } : l)));
      if (willLeave) {
        const kind = next as "confirmed" | "dismissed";
        setLeaving((prev) => ({ ...prev, [lead.id]: { kind, phase: "swipe" } }));
        setTimeout(() => setLeaving((prev) => (prev[lead.id] ? { ...prev, [lead.id]: { kind, phase: "collapse" } } : prev)), 380);
        setTimeout(() => setLeaving((prev) => { const n = { ...prev }; delete n[lead.id]; return n; }), 720);
      }
    } catch {
      toast.error("Failed to update lead");
    }
  };
  const confirmedCount = leads.filter((l) => l.status === "confirmed").length;

  // Category counts within the current status/severity view, so "New"
  // shows what's actually waiting in each bucket. Empty buckets are hidden.
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const l of leads) {
      const inView = (status === "all" || (status === "open" ? l.status !== "dismissed" : l.status === status)) && l.severity >= minSeverity;
      if (inView) counts[l.category] = (counts[l.category] || 0) + 1;
    }
    return counts;
  }, [leads, status, minSeverity]);
  const categoryOptions = Object.keys(LEAD_CATEGORY_LABELS).filter((k) => categoryCounts[k]);
  const inViewTotal = Object.values(categoryCounts).reduce((a, b) => a + b, 0);

  const [noteLead, setNoteLead] = useState<LeadListItem | null>(null);
  const addNote = (lead: LeadListItem) => setNoteLead(lead);
  const saveNote = async (note: string) => {
    if (!noteLead) return;
    try {
      const updated = await api.updateLead(investigationId, noteLead.id, { note });
      setLeads((prev) => prev.map((l) => (l.id === noteLead.id ? { ...l, ...updated } : l)));
    } catch {
      toast.error("Failed to save note");
      throw new Error("save failed");
    }
  };

  if (reanalyzing) {
    return <AnalyzingState title="Re-analyzing every call" body="Leads are re-read from each call. Confirmed and dismissed leads keep their decisions; new ones land in the inbox as calls finish." />;
  }

  return (
    <div className={cn("max-w-5xl", nowPlaying && "pb-40")}>
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <Dropdown
          size="sm"
          dense
          options={[{ value: "", label: `All categories (${inViewTotal})` }, ...categoryOptions.map((k) => ({ value: k, label: `${LEAD_CATEGORY_LABELS[k]} (${categoryCounts[k]})` }))]}
          value={categoryOptions.includes(category) ? category : ""}
          onChange={setCategory}
        />
        <Dropdown
          size="sm"
          dense
          options={[
            { value: "new", label: `New (${leads.filter((l) => l.status === "new").length})` },
            { value: "confirmed", label: `Confirmed (${confirmedCount})` },
            { value: "dismissed", label: `Dismissed (${leads.filter((l) => l.status === "dismissed").length})` },
            { value: "open", label: "Open (new + confirmed)" },
            { value: "all", label: "All" },
          ]}
          value={status}
          onChange={setStatus}
        />
        <Dropdown size="sm" dense options={[1, 2, 3, 4, 5].map((s) => ({ value: String(s), label: `Severity ≥ ${s}` }))} value={String(minSeverity)} onChange={(v) => setMinSeverity(Number(v))} />
        <span className="text-sm text-base-content/60 ml-auto">{filtered.length} of {leads.length}</span>
        <Button
          variant="secondary"
          size="sm"
          title={confirmedCount ? `Download a lead sheet of the ${confirmedCount} confirmed lead${confirmedCount === 1 ? "" : "s"}` : "Confirm leads to build a lead sheet"}
          onClick={() => downloadFile(api.leadSheetUrl(investigationId, { all: status === "all" }), "lead-sheet.pdf").catch((e) => toast.error(e.message))}
        >
          <ArrowDownTrayIcon className="size-4" /> Lead sheet{status === "all" ? " (all)" : confirmedCount ? ` (${confirmedCount})` : ""}
        </Button>
      </div>
      {leads.length > 0 && <AiDisclaimer kind="leads" className="mb-3" />}
      {status === "confirmed" && confirmedCount > 0 && (
        <p className="mb-3 text-xs text-base-content/60 flex items-center gap-1.5">
          <CheckCircleIcon className="size-4 text-green-600 dark:text-green-400" />
          Your confirmed leads: what the lead sheet exports and what the chat treats as verified.
        </p>
      )}
      {onSeen && <UpdatesBanner tab="leads" count={updates} since={since} onDismiss={onSeen} clearing={clearing} className="mb-3" />}
      {loading ? (
        <div className="animate-pulse space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-24 bg-base-300 rounded-xl" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-base-300 p-10 text-center text-base-content/60">
          {leads.length === 0 ? "No leads yet. They appear as calls finish analysis." : status === "new" ? "Inbox zero. Every lead has a decision." : "Nothing matches these filters."}
        </div>
      ) : (
        <ul>
          {ordered.map((l, i) => (
            <Fragment key={l.id}>
            {reportLeads.length > 0 && l.id === reportLeads[0].id && (
              <li className={cn("pb-3", evidenceLeads.length > 0 && "pt-4")}>
                <h3 className="text-sm font-semibold text-base-content">From your case documents ({reportLeads.length})</h3>
                <p className="text-xs text-base-content/60 mt-0.5 max-w-2xl">
                  Discrepancies and new information pulled from official reports and filings — context findings, not intercepted
                  evidence. Severity here means how much it changes the picture. Everything still appears in the discovery export.
                </p>
              </li>
            )}
            <li
              style={revealProps(i).style}
              className={cn(
                "reveal grid transition-[grid-template-rows] duration-300 ease-in-out",
                leaving[l.id]?.phase === "collapse" ? "grid-rows-[0fr]" : "grid-rows-[1fr]",
                // The dismiss chooser overlays the next card; lift this row above it.
                dismissing?.id === l.id ? "relative z-30" : "relative z-0"
              )}
            >
            {/* overflow-hidden only while collapsing, so the popover isn't clipped. */}
            <div className={cn("min-h-0 pb-3", leaving[l.id] && "overflow-hidden")}>
            <div
              className={cn(
                // box-shadow is the NEW ring; it has to be in this list because two transition-[…] classes on one element don't merge.
                "rounded-xl border bg-base-100 p-4 transition-[transform,opacity,background-color,border-color,box-shadow] duration-[380ms] ease-out",
                // Swipe: confirmed cards go right, dismissed go left, both fading as they travel.
                leaving[l.id] ? "opacity-0" : "opacity-100",
                leaving[l.id]?.kind === "confirmed" && "translate-x-[60%] border-green-500 bg-green-50 dark:bg-green-950/40",
                leaving[l.id]?.kind === "dismissed" && "-translate-x-[60%] border-base-300 bg-base-200",
                !leaving[l.id] && "border-base-300",
                !leaving[l.id] && isNewSince(l.created_at, since) && !clearing && NEW_ROW_CLASS,
                l.status === "dismissed" && !leaving[l.id] && "opacity-60"
              )}
            >
              <div className="flex items-center gap-2 flex-wrap">
                {isNewSince(l.created_at, since) && <NewPill clearing={clearing} />}
                <span className={cn("text-xs font-semibold px-2 py-0.5 rounded", SEVERITY_CLASSES[l.severity])}>severity {l.severity}</span>
                <span className="text-sm font-medium text-base-content">{LEAD_CATEGORY_LABELS[l.category]}</span>
                {l.source === "manual" && <span className="text-[11px] px-1.5 rounded bg-base-200">manual</span>}
                {l.status === "confirmed" && <span className="text-[11px] px-1.5 rounded bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-400">confirmed</span>}
                {l.status === "dismissed" && <span className="text-[11px] px-1.5 rounded bg-base-200 text-base-content/60">dismissed{l.dismiss_reason ? ` · ${DISMISS_REASONS.find((r) => r.value === l.dismiss_reason)?.label.toLowerCase() || l.dismiss_reason}` : ""}</span>}
                <span className="ml-auto text-xs text-base-content/60">
                  {l.fileName}
                  {!l.call_id && l.page ? ` · p. ${l.page}` : ""}
                  {l.callDate ? ` · ${new Date(l.callDate).toLocaleDateString()}` : ""}
                  {l.inmate ? ` · ${l.inmate}` : ""}
                </span>
              </div>
              <blockquote className="mt-2 border-l-2 border-base-300 pl-3 italic text-base-content/90">"{l.quote_translated || l.quote}"</blockquote>
              {l.quote_translated && <p className="pl-3 text-xs text-base-content/50 italic">original: "{l.quote}"</p>}
              <p className="mt-1 text-sm text-base-content/70">{l.explanation}</p>
              {l.note && <p className="mt-1 text-xs text-base-content/70">Note: {l.note}</p>}
              <div className="mt-2 flex items-center gap-1 text-sm">
                {l.video_anchor && (
                  // Video leads open the footage at the moment.
                  <button
                    className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-300 hover:underline mr-1"
                    onClick={() => navigate(`/investigations/${investigationId}/files/${l.file_id}?lead=${l.id}`)}
                  >
                    <PlayIcon className="size-4" /> Open footage at {formatMs(l.start_ms)}
                  </button>
                )}
                {l.call_id && !l.video_anchor && (
                  <button
                    className={cn("inline-flex items-center gap-1 text-blue-600 dark:text-blue-300 hover:underline mr-1", playingLeadId === l.id && nowPlaying?.callId === l.call_id && audioPlaying && "font-semibold")}
                    onClick={() => {
                      setPlayingLeadId(l.id);
                      play(investigationId, l.call_id!, l.start_ms || 0);
                    }}
                  >
                    <PlayIcon className="size-4" /> {playingLeadId === l.id && nowPlaying?.callId === l.call_id && audioPlaying ? "Playing" : `Play from ${formatMs(l.start_ms)}`}
                  </button>
                )}
                {!l.call_id && !l.video_anchor && (
                  // Document leads: open the file viewer on the flagged page.
                  <button
                    className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-300 hover:underline mr-1"
                    onClick={() => navigate(`/investigations/${investigationId}/files/${l.file_id}${l.page ? `?page=${l.page}` : ""}`)}
                  >
                    <DocumentTextIcon className="size-4" /> {l.page ? `Open p. ${l.page}` : "Open file"}
                  </button>
                )}
                <TriageButton kind="confirm" active={l.status === "confirmed"} onClick={() => setLeadStatus(l, l.status === "confirmed" ? "new" : "confirmed")} />
                <span className="relative" data-dismiss-chooser>
                  <TriageButton kind={l.status === "dismissed" ? "restore" : "dismiss"} onClick={() => (l.status === "dismissed" ? setLeadStatus(l, "new") : setDismissing(dismissing?.id === l.id ? null : l))} />
                  {dismissing?.id === l.id && (
                    <span className="absolute left-0 top-full mt-1 z-20 w-64 rounded-xl border border-base-300 bg-base-100 shadow-lg p-1 block">
                      <span className="block px-2 py-1 text-[11px] uppercase tracking-wider text-base-content/50">Why?</span>
                      {DISMISS_REASONS.map((r) => (
                        <button key={r.value} onClick={() => setLeadStatus(l, "dismissed", r.value)} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-base-200">
                          <span className="block text-sm text-base-content">{r.label}</span>
                          <span className="block text-[11px] text-base-content/50">{r.hint}</span>
                        </button>
                      ))}
                    </span>
                  )}
                </span>
                <TriageButton kind="note" onClick={() => addNote(l)} />
                <span className="ml-auto text-xs text-base-content/40">{Math.round(Number(l.confidence || 0) * 100)}% confidence</span>
              </div>
            </div>
            </div>
            </li>
            </Fragment>
          ))}
        </ul>
      )}

      <LeadNoteModal lead={noteLead} onClose={() => setNoteLead(null)} onSave={saveNote} />
    </div>
  );
};

export default LeadsTab;
